import { Helmet } from 'react-helmet'
import { APP } from '@/common/strings'
import { FC, useEffect, useState, useCallback } from 'react'
import { H1 } from '@/common/ui/h1'
import { Holdings } from './holdings'
import { Orders } from './orders'
import { Tabs, TabsContent, TabsTrigger } from '@/common/ui/tabs'
import { TabsList } from '@radix-ui/react-tabs'
import { useFetchTradingAccount, useListHoldings, useListOrders } from '@/trading/tradingQueries'
import useKycStore from '@/kyc/kycStore'
import { cn, seconds } from '@/common/utils/utils'
import { analytics } from '@/common/analytics/analytics'
import { HoldingT } from '@/common/types'
import { useCampaignsWithHoldings } from '@/dashboard/useCampaignsWithHoldings'
import useUserStore from '@/user/userStore'
import useAccountStore from '@/account/accountStore'
import { View, ViewSelector } from './ViewSelector'
import { PortfolioSummary } from '@/dashboard/PortfolioSummary'
import { PortfolioViewClickedAnalytics } from '@/common/analytics/events/PortfolioViewClickedAnalytics'

export function Portfolio() {
    const { user } = useUserStore()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const [view, setView] = useState<View>('Day')
    const [filteredHoldings, setFilteredHoldings] = useState<HoldingT[]>([])
    const { kycComplete } = useKycStore()
    const { data: tradingAccount } = useFetchTradingAccount(activeAccountId)
    const hasApprovedTradingAccount = tradingAccount?.status === 'APPROVED'
    const { campaignsWithHoldings } = useCampaignsWithHoldings()
    const { data: holdings } = useListHoldings(activeAccountId, {
        enabled: kycComplete && hasApprovedTradingAccount,
        refetchInterval: seconds(20),
    })
    const { data: orders } = useListOrders(activeAccountId, {
        from: user?.createdAt,
        enabled: kycComplete && hasApprovedTradingAccount,
        refetchInterval: seconds(20),
    })
    const [hidePledged, setHidePledged] = useState<boolean>(() =>
        JSON.parse(localStorage.getItem('hidePledged') ?? 'false'),
    )

    const trackTabChange = (value: string) => {
        const selectedTabName = value === 'positions' ? 'Positions' : 'Orders'
        analytics.track(`${selectedTabName} tab selected`)
    }

    useEffect(() => {
        const pledgedSecurityCodes =
            campaignsWithHoldings?.flatMap((cp) =>
                Object.keys(cp.pledges ?? {}).filter((key) => cp.pledges[key] > 0),
            ) ?? []
        const pledgedSecurities = hidePledged ? pledgedSecurityCodes : []
        const filteredHoldings =
            holdings?.holdings.filter(
                (entry: { securityCode: string }) => !pledgedSecurities.includes(entry.securityCode),
            ) ?? []
        setFilteredHoldings(filteredHoldings)
    }, [hidePledged, holdings, campaignsWithHoldings])

    type TabProps = {
        label: string
        value: string
    }
    const Tab: FC<TabProps> = ({ label, value }) => {
        return (
            <TabsTrigger
                value={value}
                className={cn(
                    'w-full py-3 rounded-b-none border-b-2 font-bold text-[#888]',
                    'data-[state=active]:border-primary-light data-[state=active]:text-foreground',
                )}
            >
                {label}
            </TabsTrigger>
        )
    }

    const handleViewChange = useCallback(
        (newView: View) => {
            if (newView === view) return
            setView(newView)
            PortfolioViewClickedAnalytics.track({ view: newView })
        },
        [view],
    )

    return (
        <>
            <Helmet>
                <title>Portfolio | {APP.title}</title>
            </Helmet>
            <div className="flex flex-col justify-between items-start gap-8 md:gap-6">
                <H1 className="mb-0">Portfolio</H1>
                <div className="hidden md:flex w-full">
                    <PortfolioSummary
                        accountId={activeAccountId}
                        hidePledged={hidePledged}
                        setHidePledged={setHidePledged}
                    />
                </div>
                <Tabs defaultValue="positions" className="flex flex-col gap-8 w-full" onValueChange={trackTabChange}>
                    <TabsList className="w-full md:w-80 p-0 h-auto flex justify-center w-[200px]">
                        <Tab label="Positions" value="positions" />
                        <Tab label="Orders" value="orders" />
                    </TabsList>
                    <div className="md:hidden grid grid-cols-1 gap-6">
                        <PortfolioSummary
                            accountId={activeAccountId}
                            hidePledged={hidePledged}
                            setHidePledged={setHidePledged}
                        />
                    </div>
                    <ViewSelector view={view} setView={handleViewChange} inputClassName="md:hidden w-[150px]" />
                    <TabsContent value="positions">
                        <Holdings holdings={filteredHoldings} view={view} />
                    </TabsContent>
                    <TabsContent value="orders">
                        <Orders orders={orders?.orders ?? []} />
                    </TabsContent>
                </Tabs>
            </div>
        </>
    )
}
